import { ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, Output, ViewChild } from '@angular/core';

// Which models the library shows: the user's own (with a sample while it renders), the ones whose
// videos they bought, or the samples they walked through (owner, 2026-10-23).
export type RenderLibraryFilter = 'mine' | 'purchases' | 'samples';

export const RENDER_LIBRARY_FILTERS: ReadonlyArray<{ id: RenderLibraryFilter; label: string }> = [
  { id: 'mine', label: 'My models' },
  { id: 'purchases', label: 'Purchases' },
  { id: 'samples', label: 'Samples' },
];

let nextMenuId = 0;

// The library's filter (Figma 28766:46464, the menu 28766:46465 — designer 2026-10-23, there with
// "All" and "Purchases"): her two sliders open a menu of the views, the one shown framed in black
// with a tick, the others in grey. An inline dropdown like the profile menu's, not a mat-menu:
// styles.scss moves every cdk-overlay-pane.
@Component({
  selector: 'app-render-library-filter',
  templateUrl: './render-library-filter.component.html',
  styleUrls: ['./render-library-filter.component.scss'],
})
export class RenderLibraryFilterComponent {
  @Input() public value: RenderLibraryFilter = 'mine';
  @Output() public readonly valueChange = new EventEmitter<RenderLibraryFilter>();

  @ViewChild('trigger') private trigger?: ElementRef<HTMLButtonElement>;
  @ViewChild('menu') private menu?: ElementRef<HTMLElement>;

  public readonly filters = RENDER_LIBRARY_FILTERS;
  public readonly menuId = `render-library-filter-${nextMenuId++}`;
  public open = false;

  constructor(
    private readonly host: ElementRef<HTMLElement>,
    private readonly changeDetector: ChangeDetectorRef,
  ) {}

  public get label(): string {
    return `Show: ${this.filters.find((filter) => filter.id === this.value)?.label ?? ''}`;
  }

  public toggle(): void {
    if (this.open) {
      this.close();
      return;
    }
    this.open = true;
    this.changeDetector.detectChanges();
    (this.items().find((item) => item.getAttribute('aria-checked') === 'true') || this.items()[0])?.focus();
  }

  public choose(filter: RenderLibraryFilter): void {
    this.close();
    if (filter !== this.value) {
      this.value = filter;
      this.valueChange.emit(filter);
    }
  }

  public close(returnFocus = true): void {
    if (!this.open) {
      return;
    }
    this.open = false;
    if (returnFocus) {
      this.trigger?.nativeElement.focus();
    }
  }

  // Arrows, Home and End move between the views; Tab leaves the menu and closes it.
  public onMenuKeydown(event: KeyboardEvent): void {
    const items = this.items();
    const index = items.indexOf(document.activeElement as HTMLElement);
    let next = -1;
    switch (event.key) {
      case 'ArrowDown':
        next = (index + 1) % items.length;
        break;
      case 'ArrowUp':
        next = (index - 1 + items.length) % items.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = items.length - 1;
        break;
      case 'Tab':
        this.close(false);
        return;
      default:
        return;
    }
    event.preventDefault();
    items[next]?.focus();
  }

  @HostListener('document:click', ['$event'])
  public onDocumentClick(event: MouseEvent): void {
    if (this.open && !this.host.nativeElement.contains(event.target as Node)) {
      this.close(false);
    }
  }

  @HostListener('keydown.escape')
  public onEscape(): void {
    this.close();
  }

  private items(): HTMLElement[] {
    return Array.from(this.menu?.nativeElement.querySelectorAll<HTMLElement>('[role="menuitemradio"]') ?? []);
  }
}
